import React, { useMemo, useState } from 'react';
import { getLabel } from "../functions/lang";
import FormattedNumber from './FormattedNumber';

// StatTableBody: reusable tbody renderer for stat tables
// Props:
// - configsLine: array of line configs (with fk_data_id and level)
// - configsColumn: array of column configs
// - tableStatData: map of values by [lineFk][colFk]
// - language: current language
// Behavior:
// - Shows a caret only for rows that have at least one direct child (an item whose level is exactly parent.level+1 and appears after it)
// - Clicking a caret toggles visibility of its descendant rows (all deeper levels until next same-or-smaller level)

const StatTableBody = ({ configsLine = [], configsColumn = [], tableStatData = {}, language, columnKey = 'fk_data_id', showTotal = false }) => {
    const [expanded, setExpanded] = useState(() => ({}));

    // Build a map of direct children (parentFk -> [childFk,...]) and also an index map to find ranges
    const { directChildrenMap, indexMap } = useMemo(() => {
        const direct = {};
        const idx = {};
        configsLine.forEach((it, i) => {
            idx[it.fk_data_id] = i;
        });

        // For each item, look ahead to see direct children (level === parent.level + 1)
        configsLine.forEach((parent, i) => {
            const children = [];
            for (let j = i + 1; j < configsLine.length; j++) {
                const candidate = configsLine[j];
                if (candidate.level === parent.level + 1) {
                    children.push(candidate.fk_data_id);
                }
                // stop if we reached an item at same or higher level than parent (cannot be its descendant)
                if (candidate.level <= parent.level) break;
            }
            if (children.length > 0) direct[parent.fk_data_id] = children;
        });

        return { directChildrenMap: direct, indexMap: idx };
    }, [configsLine]);

    // Helper to determine if a row should be visible: it's visible if all its ancestor level items are expanded
    const visibleSet = useMemo(() => {
        const vis = new Set();
        // We'll walk through configsLine keeping a stack of ancestors
        const stack = [];
        configsLine.forEach(item => {
            while (stack.length > 0 && stack[stack.length - 1].level >= item.level) stack.pop();
            // item is child of stack top if any
            const ancestor = stack.length > 0 ? stack[0] : null; // not used here
            // find closest level-1 (or any) ancestors to check expansion: we need to ensure that for each ancestor that had direct children, that ancestor is expanded
            let allowed = true;
            for (let k = 0; k < stack.length; k++) {
                const anc = stack[k];
                // if anc has direct children and is not expanded, then descendant should be hidden
                if (directChildrenMap[anc.fk_data_id] && !expanded[anc.fk_data_id]) {
                    allowed = false;
                    break;
                }
            }
            if (allowed) vis.add(item.fk_data_id);
            stack.push(item);
        });
        return vis;
    }, [configsLine, directChildrenMap, expanded]);

    const toggle = (fk) => setExpanded(prev => ({ ...prev, [fk]: !prev[fk] }));

    const colors = ['#f2ce61', '#fff2cc', '#94b8da', '#c6e0b4'];
    let colorIndex = -1;

    return (
        <tbody>
            {configsLine.map((item2, index2) => {
                if (item2.level === 1) colorIndex++;

                // Determine indent class
                let levelClass = '';
                if (item2.level === 1) levelClass = 'fw-bold px-2';
                if (item2.level === 2) levelClass = 'px-4';
                if (item2.level === 3) levelClass = 'px-5';

                // If not visible, skip
                if (!visibleSet.has(item2.fk_data_id)) return null;

                const hasDirectChildren = !!directChildrenMap[item2.fk_data_id];
                const isLevel1 = item2.level === 1;

                let total = 0;

                return (
                    <tr key={index2} style={{ backgroundColor: colors[colorIndex % colors.length] }}>
                        <td className={`fixed-td ${levelClass}`}>
                            
                            {getLabel(item2, language)}
                            {hasDirectChildren ? (
                                <button
                                    type="button"
                                    onClick={() => toggle(item2.fk_data_id)}
                                    style={{ border: 'none', background: 'transparent', padding: 0, marginLeft: 8, cursor: 'pointer' }}
                                    aria-expanded={!!expanded[item2.fk_data_id]}
                                    aria-controls={`group-${item2.fk_data_id}`}
                                    title={expanded[item2.fk_data_id] ? 'Réduire' : 'Déplier'}
                                >
                                    <span style={{ display: 'inline-block', transform: expanded[item2.fk_data_id] ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.12s' }}>▶</span>
                                </button>
                            ) : (
                                // keep spacing so labels align
                                <span style={{ display: 'inline-block', width: 16, marginLeft: 8 }} />
                            )}
                        </td>
                        {configsColumn.map((item3, index3) => {
                            const colKey = item3[columnKey];
                            const raw = tableStatData[item2.fk_data_id] && tableStatData[item2.fk_data_id][colKey];
                            const value = raw != null ? parseFloat(raw) : null;
                            if (value !== null && !Number.isNaN(value)) total += value;
                            return (
                                <td className="scrollable-td" key={index3}>
                                    {value !== null ? <FormattedNumber value={value} locale={language} decimals={2} /> : '-'}
                                </td>
                            );
                        })}
                        {showTotal ? (
                            <td>{total !== 0 ? <FormattedNumber value={total} locale={language} decimals={2} /> : '-'}</td>
                        ) : null}
                    </tr>
                );
            })}
        </tbody>
    );
};

export default StatTableBody;
